import { QueryFunction } from '@tanstack/react-query';
import {
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  getDocFromCache,
  onSnapshot,
} from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { queryClient } from './query-client';
import { addSubscription } from './subscriptions';

export type Options = {
  setTanstackError?: boolean;
};

export const createSubscriptionDoc = <T extends { id: ID }>(
  doc: () => Promise<DocumentReference<T>> | DocumentReference<T>,
  { setTanstackError = true }: Options = {},
): QueryFunction<T | undefined, string[]> => {
  return (context) => {
    getErrorReporter().addBreadcrumb({
      category: 'tanstack-query',
      message: JSON.stringify(context.queryKey),
    });

    // per-invocation: a closure-level firstRun never settles TanStack retry promises
    let firstRun = true;
    let latestSnapshotData: T;

    return new Promise(async (resolve, reject) => {
      const docRef = await doc();

      const onSuccess = (response: DocumentSnapshot<T>) => {
        latestSnapshotData = (response.data() as T) ?? null;

        if (firstRun) {
          resolve(latestSnapshotData);
          firstRun = false;
          return;
        }

        queryClient.setQueryData(context.queryKey, latestSnapshotData);
      };

      const onError = (error: FirestoreError) => {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- non-prod logging
          console.warn(
            `[Debug-Subscription] ERROR doc ${JSON.stringify(context.queryKey)} at ${performance.now().toFixed(0)}ms`,
            error,
          );
        }
        getErrorReporter().captureException(error, {
          tags: {
            source: 'firestore-subscription-doc',
            queryKey: JSON.stringify(context.queryKey),
          },
          contexts: { subscriptionDoc: { queryKey: context.queryKey } },
        });

        if (firstRun) {
          reject(error);
          firstRun = false;
          return;
        }

        // todo: when redirecting straight to detail page after creation, this will fail.
        //  Reason being is that the document isn't created yet on the BE. Has to be fixed before turning back on
        // if (setTanstackError) {
        //   // get the query we are fetching for
        //   const queryToSetErrorOn = queryClient
        //     .getQueryCache()
        //     .getAll()
        //     .find((query) => query.queryKey === context.queryKey);
        //
        //   // at least invalidate
        //   if (queryToSetErrorOn) {
        //     queryToSetErrorOn.setState({
        //       status: 'error',
        //       error,
        //     });
        //     return;
        //   }
        // }

        queryClient.invalidateQueries({
          queryKey: context.queryKey,
          exact: true,
        });
      };

      getDocFromCache(docRef)
        .then(onSuccess)
        .catch(() => {
          // this is just cache, do nothing with this error
        });

      addSubscription(onSnapshot(docRef, onSuccess, onError));
    });
  };
};
